import React, { Fragment, useMemo, useCallback } from "react";
import styled from "styled-components";
import {
  Flex,
  PopupMenu,
  Text,
  Button,
  Item,
  AssetType,
  Box,
  Asset,
} from "@thenounproject/lingo-core";

import { useFileExport, useFileCopy } from "./hooks";
import PresetExportOption from "../../components/inspector/use/PresetExportOption";
import CustomExportOption from "../../components/inspector/use/CustomExportOption";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";

const ExportTitle = styled(Flex).attrs({
  mb: "s",
  justifyContent: "space-between",
})``;

const SketchTitle = styled(Text).attrs({
  color: "grayDark",
  mb: "s",
  font: "ui.small",
})``;

type Props = {
  item: Item;
  canEdit: boolean;
  close: () => void;
  position: {
    xPos: number | string;
    yPos: number | string;
  };
};

/**
 * Presentational menu component for file export options
 */
const FileExportMenu: React.FC<Props> = ({ item, canEdit, close, position }) => {
  const { asset } = item;
  const canManageCuts = canEdit;

  const { downloadAsset, downloadStatus } = useFileExport(item);
  const { copyAsset, copyStatus } = useFileCopy(item);
  const { showModal } = useShowModal();

  const openManageModal = useCallback(() => {
    close();
    showModal(ModalTypes.MANAGE_FILECUTS, { inspectable: { item, asset } });
  }, [item, asset, showModal, close]);

  const handleDownloadAsset = useCallback(
    (_source: "preset" | "custom", type: string, size: string, dpi?: number) => {
      void downloadAsset(type, size, dpi);
    },
    [downloadAsset]
  );

  const handleCopyAsset = useCallback(
    (
      _source: "preset" | "custom",
      type: string,
      size: string,
      _asset: Asset,
      dpi?: number,
      e?: React.MouseEvent
    ) => {
      void copyAsset(type, size, dpi, e);
    },
    [copyAsset]
  );

  const renderExportOptions = useMemo(() => {
    const { meta } = asset;
    const fromSketch = AssetType.sketchTypes.has(asset.type);

    const presetOptions =
      meta.filecuts &&
      meta.filecuts.presets.map((preset, i) => (
        <PresetExportOption
          asset={asset}
          type={preset.type}
          description={preset.description}
          size={preset.size}
          dpi={preset.dpi}
          downloadAsset={handleDownloadAsset}
          copyAsset={handleCopyAsset}
          key={i}
          data-testid={`dl-btn-${i}`}
          copyStatus={copyStatus}
          downloadStatus={downloadStatus}
        />
      ));

    return (
      <Fragment>
        {fromSketch && <SketchTitle>Sketch data unavailable to download from the web</SketchTitle>}
        {presetOptions}
        <CustomExportOption
          asset={asset}
          downloadAsset={handleDownloadAsset}
          copyAsset={handleCopyAsset}
          copyStatus={copyStatus}
          downloadStatus={downloadStatus}
        />
      </Fragment>
    );
  }, [asset, handleCopyAsset, handleDownloadAsset, copyStatus, downloadStatus]);

  const popupSource = "file-export-menu";
  const { xPos, yPos } = position;

  return (
    <>
      <PopupMenu.ContextMenuAnchor xPos={xPos} yPos={yPos} data-popup-source={popupSource} />
      <PopupMenu source={popupSource} close={close} stayOpenOnClick width={287}>
        <Box p="m">
          <ExportTitle>
            <Text font="ui.smallBold">Download as</Text>
            {canManageCuts && (
              <Button
                text="Manage"
                buttonStyle="tertiary"
                size="small"
                data-test="open-filecut-manager"
                onClick={openManageModal}
              />
            )}
          </ExportTitle>
          {renderExportOptions}
        </Box>
      </PopupMenu>
    </>
  );
};

export default FileExportMenu;
